
import { Permission, Role, AccessContext, EnhancedUser } from '@/context/auth/rbacTypes';
import { getEffectivePermissions } from '@/services/api/accessControl';
import { permissionCache } from './permissionCache';

export class PermissionEngine {
  private static instance: PermissionEngine;
  private permissionsCache = new Map<number, { permissions: Set<string>; timestamp: number }>();
  private loadingUsers = new Set<number>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  
  // Event system for permission loading
  private eventListeners = new Map<string, Set<Function>>();
  
  public static getInstance(): PermissionEngine {
    if (!PermissionEngine.instance) {
      PermissionEngine.instance = new PermissionEngine();
    }
    return PermissionEngine.instance;
  }

  // Event system methods
  public addEventListener(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
  }

  public removeEventListener(event: string, callback: Function): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  private emit(event: string, data?: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in permission event listener for ${event}:`, error);
        }
      });
    }
  }

  // Simple check if permissions are loading
  public isLoading(userId: number): boolean {
    return this.loadingUsers.has(userId);
  }

  // Check if permissions are cached and valid
  public hasValidCache(userId: number): boolean {
    const cached = this.permissionsCache.get(userId);
    return cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION;
  }

  // Setup superuser permissions immediately
  public setupSuperuserPermissions(user: EnhancedUser): void {
    if (!user.is_superuser) return;
    
    console.log(`🔧 Setting up superuser permissions for user ${user.id}`);
    
    // Superusers get all permissions - use a wildcard approach
    const superuserPermissions = new Set(['*']); // Wildcard permission
    
    this.permissionsCache.set(user.id, {
      permissions: superuserPermissions,
      timestamp: Date.now()
    });

    // Emit event that permissions are loaded
    this.emit('permissionsLoaded', { userId: user.id, permissions: superuserPermissions });
  }

  // Fetch user permissions from backend with improved state management
  private async fetchUserPermissions(userId: number): Promise<Set<string>> {
    try {
      console.log(`🔄 Starting permission fetch for user ${userId}`);
      this.loadingUsers.add(userId);
      this.emit('permissionsLoading', { userId });

      const cacheKey = `user-${userId}-permissions`;
      
      // Add timeout to permission fetching (reduced to 10 seconds)
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Permission fetch timeout after 10 seconds')), 10000)
      );
      
      const permissions = await Promise.race([
        permissionCache.get(cacheKey, async () => {
          console.log(`🌐 API call for permissions - user ${userId}`);
          const response = await getEffectivePermissions(userId);

        const mapped: string[] = Array.isArray(response)
          ? (response.map((perm: any) => {
              if (perm == null) return null;
              if (typeof perm === 'string') return perm.toLowerCase();
              if (typeof perm.id === 'string') return perm.id.toLowerCase();
              const resource = (perm.resource_id || perm.resource || perm.resourceName || '').toString().toLowerCase();
              const actionRaw = (perm.action_id || perm.action || perm.actionName || '').toString().toLowerCase();
              // normalize some common aliases only when building from parts
              const action = actionRaw === 'index' ? 'read' : actionRaw;
              if (resource && action) return `${resource}.${action}`;
              return null;
            }).filter(Boolean) as string[])
          : [];

        return mapped;
        }),
        timeoutPromise
      ]) as string[];

      const permissionSet = new Set<string>(permissions);
      
      // Update local cache for fast lookups
      this.permissionsCache.set(userId, {
        permissions: permissionSet,
        timestamp: Date.now()
      });

      console.log(`✅ Permissions loaded for user ${userId} (count: ${permissionSet.size})`, Array.from(permissionSet).slice(0, 30));
      
      // Emit event that permissions are loaded
      this.emit('permissionsLoaded', { userId, permissions: permissionSet });

      return permissionSet;
    } catch (error) {
      console.error(`❌ Failed to fetch permissions for user ${userId}:`, error);
      this.emit('permissionsError', { userId, error });
      return new Set<string>();
    } finally {
      this.loadingUsers.delete(userId);
    }
  }

  // Enhanced preload with explicit promise resolution and localStorage backup
  public async preloadUserPermissions(user: EnhancedUser): Promise<boolean> {
    console.log(`🚀 Preloading permissions for user ${user.id}`);
    
    if (user.is_superuser) {
      this.setupSuperuserPermissions(user);
      return true;
    }

    // Try to load from localStorage first for instant availability
    try {
      const storedPermissions = localStorage.getItem(`permissions_${user.id}`);
      if (storedPermissions) {
        const parsed = JSON.parse(storedPermissions);
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < this.CACHE_DURATION) {
          console.log(`📦 Restored permissions from localStorage for user ${user.id}`);
          this.permissionsCache.set(user.id, {
            permissions: new Set(parsed.permissions),
            timestamp: parsed.timestamp
          });
          this.emit('permissionsLoaded', { userId: user.id, permissions: new Set(parsed.permissions) });
        }
      }
    } catch (e) {
      console.warn('Failed to restore permissions from localStorage:', e);
    }

    try {
      const permissions = await this.fetchUserPermissions(user.id);
      
      // Save to localStorage for next time
      try {
        localStorage.setItem(`permissions_${user.id}`, JSON.stringify({
          permissions: Array.from(permissions),
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Failed to save permissions to localStorage:', e);
      }
      
      return true;
    } catch (error) {
      console.error(`❌ Failed to preload permissions for user ${user.id}:`, error);
      return false;
    }
  }

  // Main permission check - optimized for speed
  public hasPermission(
    user: EnhancedUser | null,
    permissionName: string,
    context?: AccessContext
  ): boolean {
    if (!user) {
      console.log(`🚫 Permission check failed: no user for ${permissionName}`);
      return false;
    }
    
    // Immediate superuser bypass with debugging
    const isSuperuser = user.is_superuser;
    console.log(`🔐 Permission check: ${permissionName} | User: ${user.id} | Superuser: ${isSuperuser} | is_superuser: ${user.is_superuser}`);
    
    if (isSuperuser) {
      console.log(`👑 Superuser bypass granted for ${permissionName}`);
      return true;
    }

    // If loading, return false (don't block UI)
    if (this.isLoading(user.id)) return false;

    // Check cache
    const cached = this.permissionsCache.get(user.id);
    if (!cached || Date.now() - cached.timestamp >= this.CACHE_DURATION) {
      return false; // No valid cache, return false
    }

    const permissions = cached.permissions;
    
    // Check for wildcard (superuser)
    if (permissions.has('*')) return true;
    
    const parts = permissionName.split('.');
    if (parts.length === 2) {
      const [resource, actionRaw] = parts;
      const action = String(actionRaw).toLowerCase();

      // Build candidate permissions including action synonyms and context-aware resource aliasing
      const candidates = new Set<string>();

      // Original
      candidates.add(`${resource}.${action}`);

      // Action synonyms (view <-> read)
      if (action === 'read') candidates.add(`${resource}.view`);
      if (action === 'view') candidates.add(`${resource}.read`);

      // Context-aware alternative resource
      const ctxResource = context?.resource as string | undefined;
      if (ctxResource && ctxResource !== resource) {
        candidates.add(`${ctxResource}.${action}`);
        if (action === 'read') candidates.add(`${ctxResource}.view`);
        if (action === 'view') candidates.add(`${ctxResource}.read`);
      }

      // Check all candidates
      for (const cand of candidates) {
        if (permissions.has(cand)) return true;
      }

      // Manage permission covers all actions for resource or context resource
      if (permissions.has(`${resource}.manage`)) return true;
      if (ctxResource && permissions.has(`${ctxResource}.manage`)) return true;

      // Diagnostic log on failure
      console.warn('❌ Permission denied', {
        userId: user.id,
        requested: permissionName,
        candidates: Array.from(candidates),
        permissionsCount: permissions.size,
        sample: Array.from(permissions).slice(0, 30),
      });
    } else {
      // Fallback direct check for unusual patterns
      if (permissions.has(permissionName)) return true;
    }
    
    return false;
  }

  // Add the missing hasResourceAccess method
  public hasResourceAccess(
    user: EnhancedUser | null,
    resource: string,
    action: string,
    context?: AccessContext
  ): boolean {
    if (!user) return false;
    
    // Immediate superuser bypass
    if (user.is_superuser) return true;
    
    // Build permission string
    const permissionName = `${resource}.${action}`;
    
    return this.hasPermission(user, permissionName, context);
  }

  // Get effective permissions for display
  public getEffectivePermissions(user: EnhancedUser): string[] {
    const cached = this.permissionsCache.get(user.id);
    if (!cached) return [];
    return Array.from(cached.permissions);
  }

  // Clear cache for a user
  public clearUserCache(userId: number): void {
    this.permissionsCache.delete(userId);
    this.loadingUsers.delete(userId);
    permissionCache.clearUser(userId);
    
    // Also clear localStorage
    try {
      localStorage.removeItem(`permissions_${userId}`);
    } catch (e) {
      console.warn('Failed to clear permissions from localStorage:', e);
    }
    
    this.emit('permissionsCleared', { userId });
  }

  // Clear all cache
  public clearAllCache(): void {
    this.permissionsCache.clear();
    this.loadingUsers.clear();
    permissionCache.clear();
    this.emit('allPermissionsCleared');
  }
}

export const permissionEngine = PermissionEngine.getInstance();
